import { useMutation } from "@apollo/client";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  phoneAtom,
  useMobileAuthAtomValue,
  useSetAuthenticationDialogCurrentScreenAtom,
  useSetIsAuthenticationDialogAtom,
  useSetMobileAuthAtomAtom,
  useStytchTokenTypeValue,
  useUserSessionSetAtom
} from "@RHCommerceDev/hooks/atoms";
import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import memoize from "@RHCommerceDev/utils/memoize";
import { useStytch, useStytchUser } from "@stytch/react";
import { SmsCodeVerify } from "@RHCommerceDev/component-standard-fields/SmsCodeVerify";
import { stytchSuccessMutation } from "graphql-client/queries/user";
import { useAtomValue } from "jotai";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useEnv } from "hooks/useEnv";
import useGetCartProjectLazyQuery from "@RHCommerceDev/hooks/queries/useGetCartProjectLazyQuery";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";
import useLocale from "hooks/useLocale/useLocale";

const SmsCodeHandler = () => {
  const env = useEnv();
  const mobileAuthDetails = useMobileAuthAtomValue();
  const setUserSessionAtom = useUserSessionSetAtom();
  const stytchUser = useStytchUser();
  const form = useForm({
    defaultValues: {
      code: ""
    }
  });
  const stytchTokenType = useStytchTokenTypeValue();
  const setMobileAuthData = useSetMobileAuthAtomAtom();
  const { isShoppingCartPage } = useIsCartPages();
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const [getCartProjection] = useGetCartProjectLazyQuery();
  const [stytchSuccess] = useMutation<Mutation>(stytchSuccessMutation, {
    context: {
      fetchOptions: {
        method: "POST"
      }
    },
    onCompleted: ({ stytchSuccess }) => {
      smsRouterHandler(stytchSuccess);
      setUserSessionAtom(prevData => ({
        ...prevData,
        ...stytchSuccess,
        ...(isShoppingCartPage && { isCartMerge: true })
      }));
      getCartProjection({ id: stytchSuccess?.rhuid });
    },
    onError() {}
  });

  const [errorMessage, setErrorMessage] = useState("");
  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const setOpenAuthenticationDialog = useSetIsAuthenticationDialogAtom();
  const phone = useAtomValue(phoneAtom);
  const stytchClient = useStytch();
  const { user } = useStytchUser();
  const locale = useLocale();
  const smsRouterHandler = useCallback(
    sessionData => {
      if (stytchUser?.user?.untrusted_metadata?.is2FA) {
        if (
          sessionData?.rhUser?.userType === "REGISTERED" &&
          !stytchUser?.user?.password?.requires_reset &&
          !Boolean(stytchTokenType === "reset_password")
        ) {
          setOpenAuthenticationDialog(false);
        } else {
          setAuthenticationDialogCurrentScreen("ForgotPassword");
        }
      } else {
        setOpenAuthenticationDialog(false);
      }
    },
    [
      setAuthenticationDialogCurrentScreen,
      setOpenAuthenticationDialog,
      stytchTokenType,
      stytchUser?.user?.password?.requires_reset,
      stytchUser?.user?.untrusted_metadata?.is2FA
    ]
  );

  const onSubmit = React.useCallback(
    async ({ code }) => {
      if (code.length === 0) {
        return;
      }
      const methodId = mobileAuthDetails?.method_id;
      try {
        await stytchClient.otps.authenticate(code, methodId, {
          session_duration_minutes: 129600
        });

        await stytchClient.user.update({
          name: {
            first_name: user?.name?.first_name,
            last_name: user?.name?.last_name
          },
          untrusted_metadata: {
            is2FA: true
          }
        });

        await stytchSuccess();
      } catch (error: any) {
        if (stytchData?.Stytch_Error_Object.hasOwnProperty(error.error_type)) {
          setErrorMessage(stytchData?.Stytch_Error_Object[error.error_type]);
        } else if (
          error.error_type === "otp_code_not_found" ||
          error.error_type === "invalid_code" ||
          error.error_type === "invalid_method_id"
        ) {
          setErrorMessage(stytchData?.Stytch_Error_Object["invalid_code"]);
        } else if (error.error_type === "phone_number_not_found") {
          setErrorMessage(
            stytchData?.Stytch_Error_Object?.unable_to_auth_otp_code
          );
        } else {
          console.error(error);
        }
      }
    },
    [
      mobileAuthDetails?.method_id,
      stytchClient.otps,
      stytchClient.user,
      user?.name?.first_name,
      user?.name?.last_name,
      stytchSuccess,
      stytchData?.Stytch_Error_Object
    ]
  );

  useEffect(() => {
    const sendSmsCode = async () => {
      try {
        if (user?.phone_numbers[0]?.phone_number) {
          const phoneAuthDetails = await stytchClient.otps.sms.send(
            user?.phone_numbers[0]?.phone_number,
            {
              expiration_minutes: parseInt(env.STYTCH_SMS_CODE_EXPIRATION),
              ...(locale.toLowerCase().includes("fr") && { locale: "fr" })
            }
          );
          setMobileAuthData(phoneAuthDetails);
        }
      } catch (error) {
        console.error("Error sending SMS code:", error);
      }
    };

    sendSmsCode();
  }, []);
  // https://stytch.com/docs/api/send-otp-by-sms
  const handleResendCode = async ({}) => {
    try {
      form.reset();
      setErrorMessage("");
      if (user?.phone_numbers[0]?.phone_number || phone) {
        const phoneAuthDetails = await stytchClient.otps.sms.send(
          user?.phone_numbers[0]?.phone_number || (phone as string),
          {
            expiration_minutes: parseInt(env.STYTCH_SMS_CODE_EXPIRATION),
            ...(locale.toLowerCase().includes("fr") && { locale: "fr" })
          }
        );
        setMobileAuthData(phoneAuthDetails);
      }
    } catch (error: any) {
      if (stytchData?.Stytch_Error_Object.hasOwnProperty(error.error_type)) {
        setErrorMessage(stytchData?.Stytch_Error_Object[error.error_type]);
      } else console.error("Error resending code:", error);
    }
  };
  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  return (
    <div className="flex h-auto flex-col items-center justify-center">
      <Typography
        variant="h1"
        className="flex h-[23px] w-full items-center justify-center font-primary-rh-thin text-xl !font-[75] leading-[23px] tracking-[-0.4px]"
      >
        {stytchData?.VERIFY_YOUR_ACCOUNT}
      </Typography>
      <Typography className="mb-6 mr-0.5 mt-6 !gap-0 text-center">
        <p
          className={` font-primary-rhlight text-[11px] font-[500] leading-[125%] tracking-[0.4px]`}
        >
          {stytchData?.Please_enter_phone?.split(" ")?.slice(0, 12)?.join(" ")}
          <br />
          <p className="inline">
            {stytchData?.Please_enter_phone?.split(" ")?.slice(12)?.join(" ")}{" "}
            <p
              className={`inline font-primary-rhlight  text-[11px] !font-[600] leading-[13.2px] tracking-[0.4px]`}
            >
              {user?.phone_numbers[0]?.phone_number?.slice(-4) ??
                phone?.slice(-4)}{" "}
            </p>
          </p>
        </p>
      </Typography>
      <FormProvider {...form}>
        <form
          className={`flex w-80 flex-col`}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <SmsCodeVerify
            className={`*: mb-0 mt-0 w-[311px]`}
            rules={{ required: stytchData?.Request_new_Code }}
          />
          <Typography
            className={`h-[30px] w-[311px] font-rhsans text-xxsm font-[300] leading-[12.1px] tracking-[0.44px] text-red-700 ${
              errorMessage ? "visible" : "hidden"
            }`}
          >
            {errorMessage}
          </Typography>

          <Button
            type="submit"
            variant="secondary"
            disabled={form?.formState?.isSubmitting || !isFormComplete}
            className={`mb-4 mt-10 h-[48px] !w-[311px]`}
          >
            {stytchData?.SIGN_IN}
          </Button>
          <button
            className="flex h-[14px] justify-center p-0 underline decoration-[0.5px] underline-offset-2"
            onClick={handleResendCode}
          >
            <Typography
              className={`items-center justify-center text-center font-primary-rhlight text-[11px] font-[300] leading-[13.2px] tracking-[0.4px] underline`}
            >
              {stytchData?.Resend_code}
            </Typography>
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default memoize(SmsCodeHandler);
